import { PowermeterHistory } from '../../../Types/responses/powermeter-values/{sn}'
import { Breakthrough as BreakthroughModel } from '../../../Types/responses/storages/serialnumber/forecast/breakthrough'
import { ForecastPowerResponse } from '../../../Types/responses/storages/serialnumber/forecast/power'
import { WallboxHistory } from '../../../Types/responses/wallboxes/{sn}'
import { interpolatedTemperature } from '../systemDataHandler'
import { Breakthrough, ForecastPower, HistoryData, TemperatureData } from '../models/systemDataStoreModel'
import { binarySearchObject } from './binarySearch'
import { correctTimestamp, getRoundedNumber, FIFTEEN_MINUTES_MS } from './helperFunctions'

export const transformBreakthroughData = (breakthroughData: BreakthroughModel[], installationsTimezone?: string): Breakthrough[] => {
  return breakthroughData
    .map(breakthrough => {
      /** convert the derate-power-at-date to the timezone of the installation */
      const dateOfBreakthrough = new Date(
        new Date(breakthrough.deratePowerAt).toLocaleString('en-EN', { timeZone: installationsTimezone ?? 'Europe/Berlin' })
      )

      /** filter out breakthroughs that are at 0 o'clock -> not reached for that day */
      if (dateOfBreakthrough.getHours() === 0) return null

      const correctedTimeStamp = correctTimestamp(dateOfBreakthrough.valueOf())[1]
      const newBreakthrough: Breakthrough = { ...breakthrough, deratePowerAt: correctedTimeStamp }

      return newBreakthrough
    })
    .filter(b => b !== null)
    .sort((a, b) => a.deratePowerAt - b.deratePowerAt)
}

/**
 * Transform raw forecast response into a consistent, quarter-hourly time series.
 *
 * Steps performed:
 * 1. Normalize incoming timestamps to the repository time grid using `correctTimestamp`.
 * 2. Sort values chronologically and remove duplicate timestamps (keep the last value for a timestamp).
 * 3. Linearly interpolate values between consecutive forecast samples and emit values for every 15-minute step.
 * 4. Preserve zero-power samples (do not filter out power === 0).
 *
 * Input: hourly samples from backend (ForecastPowerResponse[]).
 * Output: ForecastPower[] where timestamps are aligned to 15-minute grid (multiples of 15 minutes).
 */
export const transformForecastData = (forecastData: ForecastPowerResponse[]): ForecastPower[] => {
  if (!forecastData || forecastData.length === 0) return []

  // Step 1: Normalize timestamps and keep zero-power values.
  // Use correctTimestamp to snap incoming timestamps to the repository 15-minute grid.
  const normalizedForecasts = forecastData
    .filter(item => item.time !== undefined && item.time !== null)
    .map(item => ({ time: correctTimestamp(new Date(item.time).getTime())[1], power: item.power ?? 0 }))

  // Step 2: Sort by time and remove duplicates (keep the last value for the same timestamp).
  normalizedForecasts.sort((a, b) => a.time - b.time)
  const uniqueForecasts: ForecastPower[] = []
  for (const sample of normalizedForecasts) {
    if (uniqueForecasts.length === 0 || uniqueForecasts[uniqueForecasts.length - 1].time !== sample.time) {
      uniqueForecasts.push(sample)
    } else {
      // Replace previous item for the same timestamp with the latest one.
      uniqueForecasts[uniqueForecasts.length - 1] = sample
    }
  }

  if (uniqueForecasts.length === 0) return []

  // Step 3: Linearly interpolate between consecutive samples into 15-minute steps.
  const interpolatedForecasts: ForecastPower[] = []

  for (let index = 0; index < uniqueForecasts.length - 1; index++) {
    const currentPoint = uniqueForecasts[index]
    const nextPoint = uniqueForecasts[index + 1]

    // Time delta between the two known samples.
    const timeDelta = nextPoint.time - currentPoint.time
    // Number of 15-minute steps between currentPoint (inclusive) and nextPoint (exclusive).
    const numberOfSteps = Math.max(1, Math.floor(timeDelta / FIFTEEN_MINUTES_MS))

    for (let stepIndex = 0; stepIndex < numberOfSteps; stepIndex++) {
      const timestamp = currentPoint.time + stepIndex * FIFTEEN_MINUTES_MS
      const fraction = timeDelta > 0 ? (timestamp - currentPoint.time) / timeDelta : 0

      const currentPower = currentPoint.power ?? 0
      const nextPower = nextPoint.power ?? 0
      const interpolatedPower = getRoundedNumber(currentPower + fraction * (nextPower - currentPower))

      interpolatedForecasts.push({ time: timestamp, power: interpolatedPower })
    }
  }

  // Add the final known sample (exact timestamp and power).
  const lastPoint = uniqueForecasts[uniqueForecasts.length - 1]
  interpolatedForecasts.push({ time: lastPoint.time, power: getRoundedNumber(lastPoint.power ?? 0) })

  return interpolatedForecasts
}

/**
 * Transforms history and additional data by doing some timestamp corrections and interpolating some data.
 */
export const transformExtendedHistoryData = (
  historyData: HistoryData[],
  temperatureData: TemperatureData[],
  wallboxHistory: Record<number, WallboxHistory[]>,
  powermeterHistory: Record<string, PowermeterHistory[]>,
  breakthroughData: Breakthrough[]
): {
  history: HistoryData[]
  wallboxHistory: Record<number, WallboxHistory[]>
  powermeterHistory: Record<string, PowermeterHistory[]>
} => {
  const correctedHistoryData: HistoryData[] = []
  const transformedTemperature = interpolatedTemperature(temperatureData)

  for (const element of historyData) {
    const newHistory = { ...element }
    newHistory.time = correctTimestamp(newHistory.time)[1]
    newHistory.WeatherBreakthrough = !!binarySearchObject(breakthroughData, newHistory.time, 'deratePowerAt')
    const temperature = binarySearchObject(transformedTemperature, newHistory.time, 'time')

    if (temperature?.value) {
      newHistory.Temperature = temperature.value
    }

    // const index = (newHistory.time - from.valueOf()) / FIFTEEN_MINUTES_MS
    // correctedHistoryData[index] = newHistory

    correctedHistoryData.push(newHistory)
  }

  Object.keys(wallboxHistory).forEach((wallboxKey: string) => {
    const currentWallboxData = wallboxHistory[parseInt(wallboxKey)]
    for (const element of currentWallboxData) {
      element.time = correctTimestamp(element.time)[1]
    }
  })

  Object.keys(powermeterHistory).forEach((powermeterKey: string) => {
    const currentPowermeterData = powermeterHistory[powermeterKey]
    for (const element of currentPowermeterData) {
      element.time = correctTimestamp(element.time)[1]
    }
  })

  return {
    history: correctedHistoryData,
    wallboxHistory,
    powermeterHistory,
  }
}

/**
 * Transforms history data by correcting the timestamps
 */
export const transformHistoryData = (
  historyData: HistoryData[]
): {
  history: HistoryData[]
} => {
  const correctedHistoryData: HistoryData[] = []

  for (const element of historyData) {
    const newHistory = { ...element }
    newHistory.time = correctTimestamp(newHistory.time)[1]
    correctedHistoryData.push(newHistory)
  }

  return {
    history: correctedHistoryData,
  }
}

/**
 * Formats history values by rounding, making sure they are not negative and multiplying them with a factor.
 */
export const roundPositiveAndScale = (value: number, factor: number): number => {
  return getRoundedNumber(Math.max(0, value * factor))
}

/**
 * Transforms the forecast data based on the desired resolution.
 * If the resolution is 15 minutes, it creates additional entries for each 15 minute interval.
 */
export const transformPvForecastDataBasedOnResolution = (data: ForecastPowerResponse[], resolution: 60 | 15): ForecastPowerResponse[] => {
  if (resolution === 60) return data

  return data.flatMap(entry => {
    const base = new Date(entry.time)
    return [0, 15, 30, 45].map(offset => ({
      ...entry,
      time: base.getTime() + offset * 60_000,
    }))
  })
}
